import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { CONSULTING_BOOKING_URL, AIOS_WALKTHROUGH_URL } from '../lib/links';
import { HoneypotField, isHoneypotTripped } from '../lib/honeypot';

// Where leads go now. AuraOS owns the intake: one POST saves the submission
// AND sends the notification, so the two can't come apart. Same endpoint
// shape the ROI calculator posts to.
const AIOS_ENDPOINT = 'https://app.brandctl.com/api/forms/contact-lead';

// Canonical service list. AuraOS leads as the flagship product and, like
// Operations Consulting, is a "booking shortcut" — selecting it skips the
// intake and drops a straight-to-scheduling CTA (the free 15-minute AuraOS
// walkthrough) so AuraOS interest goes directly to a call. Operations
// Consulting shortcuts to the paid deep-dive booking the same way.
const SERVICES = [
  'AuraOS',
  'App Development',
  'Intelligent Agents',
  'Web Design',
  'Operations Consulting',
] as const;

type Service = (typeof SERVICES)[number];

type Step = 'intake' | 'contact' | 'done';

const formatBudget = (v: number) => (v >= 30 ? '$30K+' : `$${v}K`);
const formatUSD = (n: number): string =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// Web design pricing constants. Comparison tiers are calibrated against
// 2026 market rates: small-custom builds run $5–15k for a 5-page site
// (~$2k/page mid-range), high-end specialized agencies $15–30k+ for 5
// pages (~$4.5k/page mid-range). The optional retainer is BRANDCENTRAL's
// own ongoing-maintenance offer, $200/page/month — kept out of the
// upfront total but mentioned in the breakdown for transparency.
const WEB_PRICE_PER_PAGE = 450;
const WEB_ADDON_PRICE_PER_PAGE = 75;
const COMP_PREMIUM_PER_PAGE = 4500; // High-end specialized agency
const COMP_MIDTIER_PER_PAGE = 2000; // Small custom site
const WEB_RETAINER_PER_PAGE = 200;  // Optional monthly retainer (off the upfront)


interface InteractiveContactFormProps {
  /**
   * When true, suppress this component's own SVG perimeter glow. The
   * outer container becomes a thin wrapper with no padding so the
   * embedding context (the popup modal) can render the border itself
   * around the *visible* scroll area — without this, a long form
   * scrolled to the middle would show no top or bottom of the glow
   * because the SVG scrolls with the form content.
   */
  inModal?: boolean;
  /**
   * When set, the service is pre-locked. The "01 · What do you need?"
   * step is hidden and remaining steps re-numbered (01/02/03 instead
   * of 02/03/04). Used by the four service landing pages so visitors
   * arriving on /web-design only see the Web Design intake — they
   * can't accidentally fill out the wrong service path.
   */
  lockedService?: Service;
  /**
   * Presentation only. 'dark' (default) is the original zinc-950 glass
   * card used everywhere; 'light' re-skins the SAME form as a white card
   * for light-themed surfaces (currently the reskinned home page). This
   * flag never touches state, validation, or submission — only class
   * names — so the backend wiring is identical across variants.
   */
  variant?: 'dark' | 'light';
}

export const InteractiveContactForm = ({
  inModal = false,
  lockedService,
  variant = 'dark',
}: InteractiveContactFormProps = {}) => {
  const light = variant === 'light';
  const [step, setStep] = useState<Step>('intake');
  // If a service is pre-locked from the embedding page, seed the state
  // with it so all the conditional UI (Web Design slider, Consulting
  // booking CTA, etc.) renders immediately without the user picking.
  const [service, setService] = useState<Service | null>(lockedService ?? null);

  // Non–Web-Design intake
  const [budget, setBudget] = useState(10);
  const [budgetTouched, setBudgetTouched] = useState(false);

  // Web Design intake
  const [pageCount, setPageCount] = useState(5);
  const [pageCountTouched, setPageCountTouched] = useState(false);
  const [addonContent, setAddonContent] = useState(false);
  const [addonSeo, setAddonSeo] = useState(false);

  // Contact step
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Consent — auto-checked. Policy + Terms required, Blog optional.
  const [consentPolicy, setConsentPolicy] = useState(true);
  const [consentTerms, setConsentTerms] = useState(true);
  const [consentBlog, setConsentBlog] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Honeypot — hidden field; bots auto-fill and trip the bot-check at submit.
  const [honeypot, setHoneypot] = useState('');

  const isWebDesign = service === 'Web Design';
  const isConsulting = service === 'Operations Consulting';
  const isAios = service === 'AuraOS';

  // ── Presentation tokens ─────────────────────────────────────────
  // Every entry is `light ? <new light classes> : <exact original dark
  // classes>` so the dark variant (service pages) is byte-for-byte
  // unchanged. Nothing below affects behavior.
  const t = {
    label: light ? 'text-[#9a9b9e]' : 'text-white/40',
    labelStrong: light ? 'text-[#57585b]' : 'text-white/50',
    accent: light ? 'text-[#57585b]' : 'text-white/70',
    strong: light ? 'text-[#171718]' : 'text-white',
    body: light ? 'text-[#57585b]' : 'text-white/60',
    faint: light ? 'text-[#9a9b9e]' : 'text-white/30',
    pct: light ? 'text-[#c9cacd]' : 'text-white/30',
    divider: light ? 'border-[#e2e1de]' : 'border-white/10',
    chipActive: light ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-black border-white',
    chipIdle: light
      ? 'bg-[#fbfbfb] border-[#e2e1de] text-[#57585b] hover:border-[#111] hover:text-[#111]'
      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white',
    // `ia-btn-solid` forces white text even when the button is an <a> — the
    // global `.ht-root a { color: inherit }` rule outranks Tailwind's
    // text-white and would otherwise grey out the booking-link CTAs.
    primaryBtn: light
      ? 'ia-btn-solid bg-[#111] text-white hover:bg-black'
      : 'bg-white text-black hover:bg-white/90',
    input: light
      ? 'w-full bg-[#fbfbfb] border border-[#dedede] rounded-lg p-4 text-[#171718] placeholder:text-[#9a9b9e] focus:outline-none focus:border-[#171718] focus:bg-white transition-colors'
      : 'w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white placeholder:text-white/30 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] transition-colors',
    cardFill: light ? 'bg-[#f7f7f6]' : 'bg-white/[0.06]',
    lockBox: light ? 'border-[#e8e7e4] bg-[#fbfbfb]' : 'border-white/15 bg-white/5',
    strike: light ? 'text-[#8d8e92] decoration-black/25' : 'text-white/50 decoration-white/30',
    error: light ? 'text-red-600' : 'text-red-400/80',
    amber: light ? 'text-amber-600' : 'text-amber-400/85',
    amberDot: light ? 'bg-amber-500' : 'bg-amber-400/85',
  };

  // ── Web Design pricing math ─────────────────────────────────────
  const pricing = useMemo(() => {
    const addons = (addonContent ? 1 : 0) + (addonSeo ? 1 : 0);
    const baseLine = pageCount * WEB_PRICE_PER_PAGE;
    const addonsLine = pageCount * WEB_ADDON_PRICE_PER_PAGE * addons;
    const total = baseLine + addonsLine;
    const premium = pageCount * COMP_PREMIUM_PER_PAGE;
    const midtier = pageCount * COMP_MIDTIER_PER_PAGE;
    const retainer = pageCount * WEB_RETAINER_PER_PAGE; // Optional monthly retainer
    const savedVsMid = midtier - total;
    const savedPctVsMid = midtier > 0 ? Math.round((savedVsMid / midtier) * 100) : 0;
    return { addons, baseLine, addonsLine, total, premium, midtier, retainer, savedVsMid, savedPctVsMid };
  }, [pageCount, addonContent, addonSeo]);

  // ── Progress (drives the tracing-light SVG) ─────────────────────
  // Consulting has no intake step (the CTA shortcuts to booking), so its
  // intake is auto-satisfied just by selecting the service.
  const progressFlags = useMemo(() => {
    const intakeOk = isConsulting || isAios
      ? true
      : isWebDesign
        ? pageCountTouched
        : budgetTouched;
    return [
      service !== null,
      intakeOk,
      name.trim().length > 0,
      email.trim().length > 0,
      message.trim().length > 0,
    ];
  }, [service, isWebDesign, isConsulting, isAios, pageCountTouched, budgetTouched, name, email, message]);
  const fillRatio = progressFlags.filter(Boolean).length / progressFlags.length;

  // ── Step gates ──────────────────────────────────────────────────
  // Consulting bypasses the contact step entirely (CTA → booking link), so
  // intakeValid stays false for that branch and the Continue button hides.
  const intakeValid =
    service !== null &&
    !isConsulting &&
    !isAios &&
    (isWebDesign ? pageCountTouched : budgetTouched);
  const contactValid =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    message.trim().length > 0 &&
    consentPolicy &&
    consentTerms;

  const handleProceed = () => {
    if (intakeValid) setStep('contact');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactValid || submitting) return;
    // Honeypot tripwire — real visitors don't fill hidden inputs.
    // Pretend success so bots can't probe for the trap; the network
    // call is silently skipped and no lead is created.
    if (isHoneypotTripped(honeypot)) {
      setStep('done');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // Field names are snake_case because on AuraOS a field's name IS the merge
      // tag: `money` here fills {{money}} in the notification template. Anything
      // not declared on the form is dropped server-side, so adding a key here
      // means adding the field in AuraOS → Forms too.
      //
      // money/scope are composed here rather than in the template because
      // merge tags have no conditionals, and the Web Design branch reads
      // differently from every other service. This is where the pricing math
      // already lives, so it's where the sentence gets written.
      const payload = {
        service: service ?? '',
        money: isWebDesign
          ? `${formatUSD(pricing.total)} total`
          : `$${budget}k bucket`,
        budget: String(isWebDesign ? pricing.total : budget),
        scope: isWebDesign
          ? [
              `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`,
              `content writing: ${addonContent ? `yes (+$${WEB_ADDON_PRICE_PER_PAGE}/pg)` : 'no'}`,
              `on-page SEO: ${addonSeo ? `yes (+$${WEB_ADDON_PRICE_PER_PAGE}/pg)` : 'no'}`,
            ].join(' · ')
          : '',
        page_count: isWebDesign ? String(pageCount) : '',
        addon_content: isWebDesign ? (addonContent ? 'yes' : 'no') : '',
        addon_seo: isWebDesign ? (addonSeo ? 'yes' : 'no') : '',
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        // Sent as words, not booleans: these land in an email and in the
        // submissions table, where "true" reads worse than "yes".
        consent_policy: consentPolicy ? 'yes' : 'no',
        consent_terms: consentTerms ? 'yes' : 'no',
        consent_blog: consentBlog ? 'yes' : 'no',
        // Server-side honeypot, checked before anything else is parsed.
        _hp: honeypot,
        _source: typeof window !== 'undefined' ? window.location.href : '',
      };

      const res = await fetch(AIOS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        retryAfter?: number;
      };

      if (!res.ok || result.ok === false) {
        // The limiter is per-IP and generous; anyone hitting it is either
        // testing or on a shared address, and both deserve a way through.
        throw new Error(
          result.error === 'rate_limited'
            ? 'That is a few requests in a row. Give it a minute, or email hello@brandctl.com and we will pick it up there.'
            : result.error || 'Something went wrong sending your request.',
        );
      }

      trackEvent('generate_lead', {
        service: service ?? 'Unknown',
        method: 'contact_form',
      });
      setStep('done');
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Something went wrong sending your request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // OUTER container. On the homepage (inModal=false), it's 5px larger
    // than the inner panel so the SVG draws its perimeter strokes 5px
    // outside the panel — visible against the section's black bg. The
    // light variant uses a plain white card with a border + shadow, so
    // the tracing-light SVG is suppressed there (it's meaningless on
    // white). Inside the modal (inModal=true), we drop the padding +
    // suppress the SVG entirely so the modal panel can render its OWN
    // border around the visible scroll area.
    <div
      className={
        inModal
          ? 'relative w-full max-w-[480px]'
          : 'relative w-full max-w-[490px] p-[5px]'
      }
    >
      {!inModal && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          style={{ overflow: 'visible' }}
          aria-hidden="true"
        >
          {/* Light variant: a multicolor theme gradient that slowly rotates
              so the colors flow around the border while the trace (driven by
              fillRatio below) extends as the visitor fills the form. */}
          {light && (
            <defs>
              <linearGradient id="ht-form-glow" gradientUnits="objectBoundingBox" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#ff9e6b" />
                <stop offset="0.33" stopColor="#f5749f" />
                <stop offset="0.66" stopColor="#9a7bf0" />
                <stop offset="1" stopColor="#5f7bf0" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="rotate"
                  from="0 0.5 0.5"
                  to="360 0.5 0.5"
                  dur="7s"
                  repeatCount="indefinite"
                />
              </linearGradient>
            </defs>
          )}
          <rect
            x="0.75"
            y="0.75"
            width="calc(100% - 1.5px)"
            height="calc(100% - 1.5px)"
            rx="20"
            ry="20"
            fill="none"
            stroke={light ? 'rgba(20,20,24,0.07)' : 'rgba(255,255,255,0.35)'}
            strokeWidth="1.5"
          />
          <rect
            x="0.75"
            y="0.75"
            width="calc(100% - 1.5px)"
            height="calc(100% - 1.5px)"
            rx="20"
            ry="20"
            fill="none"
            stroke={light ? 'url(#ht-form-glow)' : 'white'}
            strokeWidth={light ? 3 : 1.5}
            pathLength={100}
            strokeDasharray="100"
            strokeDashoffset={100 - fillRatio * 100}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.7s cubic-bezier(0.2, 0.8, 0.2, 1)',
              filter: light
                ? 'drop-shadow(0 0 5px rgba(232,120,180,0.6)) drop-shadow(0 0 14px rgba(120,130,240,0.5))'
                : 'drop-shadow(0 0 6px rgba(255,255,255,0.85)) drop-shadow(0 0 14px rgba(255,255,255,0.4))',
            }}
          />
        </svg>
      )}

      {/* Fully opaque panel. On the homepage we keep the rounded
          corners + bg-zinc-950 since the SVG draws the glow outside.
          The light variant is a white card with border + shadow.
          Inside the modal we drop the bg + rounding because the
          MODAL panel renders the dark surface + border, and a nested
          rounded rect would create a visible double-border. */}
      <div
        className={
          inModal
            ? 'relative w-full'
            : light
              ? 'relative rounded-2xl bg-white p-8 shadow-[0_24px_60px_rgba(65,53,80,0.16)]'
              : 'relative rounded-2xl bg-zinc-950 p-8'
        }
      >
        {step === 'intake' && (
          <div className="space-y-8">
            <div className="flex items-baseline justify-between">
              <div>
                <p className={`text-[11px] uppercase tracking-[0.22em] ${t.label} font-bold`}>
                  Project Intake
                </p>
                <p className={`font-hanken italic ${t.accent} text-base mt-1`}>
                  Two quick steps.
                </p>
              </div>
              {/* Fill % hidden on locked forms — the pre-completed service
                  step makes it start at 20%, which reads as noise when the
                  visitor never chose anything. */}
              {!lockedService && (
                <span className={`font-hanken ${t.faint} text-sm tabular-nums`}>
                  {Math.round(fillRatio * 100)}%
                </span>
              )}
            </div>

            {/* 01 — Service. Hidden when the form is embedded in a
                service-specific landing page (lockedService prop set);
                a static "Service: …" badge shown instead so the visitor
                sees what they're filling out without the option to
                accidentally switch tracks. */}
            {lockedService ? (
              <div className={`rounded-lg border p-3 flex items-center justify-between ${t.lockBox}`}>
                <div>
                  <p className={`text-[10px] uppercase tracking-[0.22em] ${t.labelStrong} font-bold`}>
                    Service
                  </p>
                  <p className={`font-hanken ${t.strong} text-lg leading-tight`}>
                    {lockedService}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
                  Locked
                </span>
              </div>
            ) : (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  01 · What do you need?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {SERVICES.map((s) => {
                    const active = service === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setService(s)}
                        // min-h-[44px] meets WCAG 2.5.5 touch-target size
                        // (Level AAA but quoted by Lighthouse). Padding stays
                        // visually tight; the min-height enforces the
                        // 44x44 hit area without inflating the look.
                        className={`min-h-[44px] py-3 px-4 rounded-lg border text-xs font-inter font-medium transition-colors text-left ${
                          active ? t.chipActive : t.chipIdle
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 02 — Web Design pages / Consulting booking CTA / standard budget.
                Three-way conditional. Consulting skips intake entirely and
                presents the booking CTA instead of a slider (Apr 27 brief). */}
            {isWebDesign ? (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '01' : '02'} · How many pages?
                </p>
                <div className="space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className={`font-hanken ${t.strong} text-5xl leading-none tabular-nums`}>
                      {pageCount}
                      <span className={`text-2xl ${t.faint} ml-2`}>{pageCount === 1 ? 'page' : 'pages'}</span>
                    </span>
                    <span className={`text-[11px] uppercase tracking-[0.22em] ${t.label}`}>
                      {pageCountTouched ? 'looks good' : 'drag to set'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={50}
                    step={1}
                    value={pageCount}
                    onChange={(e) => {
                      setPageCount(Number(e.target.value));
                      setPageCountTouched(true);
                    }}
                    onMouseDown={() => setPageCountTouched(true)}
                    onTouchStart={() => setPageCountTouched(true)}
                    className={`interactive-slider w-full ${light ? 'is-light' : ''}`}
                    aria-label="Page count"
                  />
                  <div className={`flex justify-between text-[10px] uppercase tracking-[0.22em] ${t.faint}`}>
                    <span>1 page</span>
                    <span>50 pages</span>
                  </div>
                </div>
              </div>
            ) : isConsulting ? (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '01' : '02'} · Book a consulting call
                </p>
                <div className="space-y-4">
                  <p className={`font-hanken italic ${t.accent} text-base leading-snug`}>
                    Skip the form. Grab a 1-hour working session
                    ($497) and we&rsquo;ll dig into your operations live.
                  </p>
                  <a
                    href={CONSULTING_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full py-4 font-inter font-bold text-sm uppercase tracking-widest rounded-full text-center transition-colors ${t.primaryBtn}`}
                  >
                    Book Consulting Call
                  </a>
                  <p className={`text-[10px] uppercase tracking-[0.22em] ${t.faint} text-center`}>
                    Opens scheduling · no payment required to book
                  </p>
                </div>
              </div>
            ) : isAios ? (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '01' : '02'} · Book your AuraOS walkthrough
                </p>
                <div className="space-y-4">
                  <p className={`font-hanken italic ${t.accent} text-base leading-snug`}>
                    Skip the form. Grab a free 15-minute walkthrough and
                    we&rsquo;ll show you the approval queue running live on
                    our own business first.
                  </p>
                  <a
                    href={AIOS_WALKTHROUGH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full py-4 font-inter font-bold text-sm uppercase tracking-widest rounded-full text-center transition-colors ${t.primaryBtn}`}
                  >
                    Book AuraOS Walkthrough
                  </a>
                  <p className={`text-[10px] uppercase tracking-[0.22em] ${t.faint} text-center`}>
                    Opens scheduling · free, no payment required
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '01' : '02'} · Estimated budget
                </p>
                <div className="space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className={`font-hanken ${t.strong} text-5xl leading-none tabular-nums`}>
                      {formatBudget(budget)}
                    </span>
                    <span className={`text-[11px] uppercase tracking-[0.22em] ${t.label}`}>
                      {budgetTouched ? 'looks good' : 'drag to set'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={3}
                    max={30}
                    step={1}
                    value={budget}
                    onChange={(e) => {
                      setBudget(Number(e.target.value));
                      setBudgetTouched(true);
                    }}
                    onMouseDown={() => setBudgetTouched(true)}
                    onTouchStart={() => setBudgetTouched(true)}
                    className={`interactive-slider w-full ${light ? 'is-light' : ''}`}
                    aria-label="Estimated budget"
                  />
                  <div className={`flex justify-between text-[10px] uppercase tracking-[0.22em] ${t.faint}`}>
                    <span>$3K</span>
                    <span>$30K+</span>
                  </div>
                </div>
              </div>
            )}

            {/* 03 — Web Design add-ons */}
            {isWebDesign && (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '02' : '03'} · Add-ons{' '}
                  <span className={`${t.faint} font-medium tracking-normal normal-case`}>
                    +${WEB_ADDON_PRICE_PER_PAGE}/page each
                  </span>
                </p>
                <div className="space-y-2">
                  <AddonRow
                    label="I need help with page content"
                    checked={addonContent}
                    onChange={setAddonContent}
                    light={light}
                  />
                  <AddonRow
                    label="I want to optimize for SEO"
                    checked={addonSeo}
                    onChange={setAddonSeo}
                    light={light}
                  />
                </div>
              </div>
            )}

            {/* 04 — Web Design pricing comparison */}
            {isWebDesign && (
              <div>
                <p className={`text-xs uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                  {lockedService ? '03' : '04'} · What this typically costs
                </p>
                <div className={`rounded-xl border overflow-hidden ${t.divider}`}>
                  <ComparisonRow
                    tier="High-end specialized agency"
                    perPage={COMP_PREMIUM_PER_PAGE}
                    pages={pageCount}
                    total={pricing.premium}
                    muted
                    light={light}
                  />
                  <ComparisonRow
                    tier="Small custom site"
                    perPage={COMP_MIDTIER_PER_PAGE}
                    pages={pageCount}
                    total={pricing.midtier}
                    muted
                    light={light}
                  />

                  {/* BRANDCENTRAL row — full emphasis */}
                  <div className={`${t.cardFill} border-t ${t.divider} px-4 py-4 space-y-2`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={`font-inter text-[10px] uppercase tracking-[0.22em] ${t.strong} font-bold`}>
                        BRANDCENTRAL
                      </span>
                      <span className={`font-inter text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
                        ${WEB_PRICE_PER_PAGE} × {pageCount}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={`font-inter ${t.body} text-sm`}>Pages</span>
                      <span className={`font-hanken ${t.strong} text-base tabular-nums`}>
                        {formatUSD(pricing.baseLine)}
                      </span>
                    </div>
                    {pricing.addons > 0 && (
                      <div className="flex items-baseline justify-between gap-3">
                        <span className={`font-inter ${t.body} text-sm`}>
                          Add-ons ({pricing.addons} × ${WEB_ADDON_PRICE_PER_PAGE} × {pageCount})
                        </span>
                        <span className={`font-hanken ${t.strong} text-base tabular-nums`}>
                          +{formatUSD(pricing.addonsLine)}
                        </span>
                      </div>
                    )}
                    <div className={`border-t ${t.divider} pt-2 mt-2 flex items-baseline justify-between gap-3`}>
                      <span className={`font-inter text-[10px] uppercase tracking-[0.22em] ${t.body} font-bold`}>
                        Your total
                      </span>
                      <span className={`font-hanken ${t.strong} text-3xl md:text-4xl tabular-nums leading-none`}>
                        {formatUSD(pricing.total)}
                      </span>
                    </div>
                    {pricing.savedVsMid > 0 && (
                      <p className={`font-inter text-[10px] uppercase tracking-[0.22em] ${t.labelStrong} pt-1`}>
                        You save {formatUSD(pricing.savedVsMid)} vs a small custom site · {pricing.savedPctVsMid}% less
                      </p>
                    )}
                    {/* Retainer disclosure — does NOT add to "Your total"
                        but admins can opt clients in for ongoing
                        maintenance, content updates, and SEO tuning. */}
                    <p className={`font-inter text-[10px] uppercase tracking-[0.22em] ${t.label} pt-1 border-t ${t.divider} mt-2 pt-2`}>
                      Above doesn&rsquo;t include the optional monthly
                      retainer · starts at ${WEB_RETAINER_PER_PAGE}/page/mo
                      ({formatUSD(pricing.retainer)}/mo for {pageCount}{' '}
                      {pageCount === 1 ? 'page' : 'pages'})
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Consulting + AuraOS are booking shortcuts — they end at the
                booking CTA above, so no Continue button for either. */}
            {!isConsulting && !isAios && (
              <button
                type="button"
                onClick={handleProceed}
                disabled={!intakeValid}
                className={`w-full py-4 font-inter font-bold text-sm uppercase tracking-[0.2em] rounded-full transition-colors disabled:opacity-25 disabled:cursor-not-allowed ${t.primaryBtn}`}
              >
                Continue
              </button>
            )}
          </div>
        )}

        {step === 'contact' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <HoneypotField value={honeypot} onChange={setHoneypot} />
            <div className={`flex items-start justify-between pb-5 border-b ${t.divider}`}>
              <div>
                <p className={`text-[11px] uppercase tracking-[0.22em] ${t.label} font-bold`}>
                  Almost there
                </p>
                <p className={`font-hanken ${t.strong} text-xl mt-1 leading-tight`}>
                  {service}
                  {' · '}
                  <span className="italic">
                    {isWebDesign
                      ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} · ${formatUSD(pricing.total)}`
                      : formatBudget(budget)}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep('intake')}
                className={`text-[11px] uppercase tracking-[0.22em] ${t.label} ${light ? 'hover:text-[#111]' : 'hover:text-white'} transition-colors font-bold shrink-0`}
              >
                Edit
              </button>
            </div>

            <div className="space-y-2">
              <label htmlFor="bc-name" className={`text-xs uppercase tracking-widest ${t.labelStrong} font-bold`}>
                Your name
              </label>
              <input
                id="bc-name"
                required
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={t.input}
                placeholder="First and last name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="bc-email" className={`text-xs uppercase tracking-widest ${t.labelStrong} font-bold`}>
                Business email
              </label>
              <input
                id="bc-email"
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={t.input}
                placeholder="name@yourcompany.com"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="bc-message" className={`text-xs uppercase tracking-widest ${t.labelStrong} font-bold`}>
                Tell us about your project
              </label>
              <textarea
                id="bc-message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={`${t.input} h-28 resize-none`}
                placeholder="The problem, the stack, the deadline, whatever matters."
              />
              <p className={`text-[10px] uppercase tracking-[0.22em] ${t.faint} pt-1`}>
                We respond within 24 hours, weekdays.
              </p>
            </div>

            {/* Consent — Policy + Terms required (must stay checked to submit),
                Blog/newsletter optional and pre-checked. Each row is a
                clearly-labeled, full-width target so there's no ambiguity
                about what the visitor is agreeing to. */}
            <div className={`pt-2 border-t ${t.divider}`}>
              <p className={`text-[11px] uppercase tracking-[0.22em] ${t.labelStrong} font-bold mb-3`}>
                Before you submit
              </p>
              <p className={`font-inter ${t.body} text-[13px] leading-relaxed mb-4`}>
                By submitting this form you confirm you&rsquo;ve read and
                agree to our Privacy Policy and Terms of Service. We&rsquo;ll
                only use your details to respond to your project request.
              </p>

              <div className="space-y-2.5">
                <ConsentRow
                  checked={consentPolicy}
                  onChange={setConsentPolicy}
                  required
                  light={light}
                  label={
                    <>
                      I have read and agree to the{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener"
                        className={light ? 'underline underline-offset-2 text-[#171718] hover:text-black' : 'underline underline-offset-2 text-white/95 hover:text-white'}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
                <ConsentRow
                  checked={consentTerms}
                  onChange={setConsentTerms}
                  required
                  light={light}
                  label={
                    <>
                      I have read and agree to the{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener"
                        className={light ? 'underline underline-offset-2 text-[#171718] hover:text-black' : 'underline underline-offset-2 text-white/95 hover:text-white'}
                      >
                        Terms of Service
                      </Link>
                      .
                    </>
                  }
                />
                <ConsentRow
                  checked={consentBlog}
                  onChange={setConsentBlog}
                  light={light}
                  label={
                    <>
                      Send me the{' '}
                      <Link
                        to="/blog"
                        target="_blank"
                        rel="noopener"
                        className={light ? 'underline underline-offset-2 text-[#171718] hover:text-black' : 'underline underline-offset-2 text-white/95 hover:text-white'}
                      >
                        BRANDCENTRAL newsletter
                      </Link>
                      {' '}: operations teardowns, web design notes, and AI agent
                      build reports. Unsubscribe anytime.
                    </>
                  }
                />
              </div>

              {(!consentPolicy || !consentTerms) && (
                <p className={`text-[11px] uppercase tracking-[0.22em] ${t.amber} font-bold pt-3`}>
                  Privacy Policy and Terms of Service must stay checked to submit.
                </p>
              )}
              <p className={`font-inter ${t.faint} text-[11px] leading-snug pt-3`}>
                We never sell your information. Required fields are marked with
                a small <span className={`${t.amber} font-bold`}>●</span>.
              </p>
            </div>

            <button
              type="submit"
              disabled={!contactValid || submitting}
              className={`w-full py-5 font-inter font-bold text-sm uppercase tracking-[0.2em] rounded-full transition-colors disabled:opacity-25 disabled:cursor-not-allowed ${t.primaryBtn}`}
            >
              {submitting ? 'Sending…' : 'Send Request'}
            </button>
            {submitError && (
              <p className={`text-[11px] uppercase tracking-[0.22em] ${t.error} font-bold pt-2 text-center`}>
                {submitError}
              </p>
            )}
          </form>
        )}

        {step === 'done' && (
          <div className="py-10 text-center space-y-3">
            <p className={`text-[11px] uppercase tracking-[0.22em] ${t.label} font-bold`}>
              Received
            </p>
            <p className={`font-hanken ${t.strong} text-4xl leading-tight`}>
              Thank you, <span className="italic">{name.split(' ')[0] || 'friend'}</span>.
            </p>
            <p className={`font-inter ${t.body} text-sm max-w-[320px] mx-auto`}>
              We&rsquo;ll review your {service?.toLowerCase()} project and reach out within 24 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Subcomponents ────────────────────────────────────────────────

const AddonRow = ({
  label,
  checked,
  onChange,
  light = false,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  light?: boolean;
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
      checked
        ? light ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-black border-white'
        : light ? 'bg-[#fbfbfb] border-[#e2e1de] text-[#57585b] hover:border-[#111]' : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
    }`}
  >
    <span className="font-inter text-sm">{label}</span>
    <span
      className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center text-[12px] font-bold ${
        checked
          ? light ? 'bg-white border-white text-[#111]' : 'bg-black border-black text-white'
          : light ? 'border-[#c9c9c6] text-transparent' : 'border-white/30 text-transparent'
      }`}
      aria-hidden="true"
    >
      ✓
    </span>
  </button>
);

const ComparisonRow = ({
  tier,
  perPage,
  pages,
  total,
  muted,
  light = false,
}: {
  tier: string;
  perPage: number;
  pages: number;
  total: number;
  muted?: boolean;
  light?: boolean;
}) => (
  <div
    className={`px-4 py-3 flex items-baseline justify-between gap-3 ${
      muted ? 'bg-transparent' : ''
    }`}
  >
    <div>
      <p className={`font-inter text-[10px] uppercase tracking-[0.22em] font-bold ${light ? 'text-[#57585b]' : 'text-white/50'}`}>
        {tier}
      </p>
      <p className={`font-inter text-[11px] mt-0.5 ${light ? 'text-[#9a9b9e]' : 'text-white/30'}`}>
        {formatUSD(perPage)} × {pages} {pages === 1 ? 'page' : 'pages'}
      </p>
    </div>
    <span className={`font-hanken text-lg tabular-nums line-through ${light ? 'text-[#8d8e92] decoration-black/25' : 'text-white/50 decoration-white/30'}`}>
      {formatUSD(total)}
    </span>
  </div>
);

const ConsentRow = ({
  checked,
  onChange,
  required,
  label,
  light = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  required?: boolean;
  label: React.ReactNode;
  light?: boolean;
}) => (
  <label
    className={`group flex items-start gap-3 cursor-pointer rounded-lg p-2.5 transition-colors ${
      checked ? (light ? 'bg-black/[0.03]' : 'bg-white/[0.04]') : ''
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only peer"
      aria-required={required}
    />
    {/* Visible 18px box. Solid when checked, hollow with a subtle
        border when not. Required boxes show a small amber dot in the
        top-right corner so visitors can see at a glance which ones must
        stay checked. */}
    <span
      className={`relative mt-px shrink-0 w-[18px] h-[18px] rounded border flex items-center justify-center text-[12px] font-bold transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 ${
        light ? 'peer-focus-visible:ring-black/30 peer-focus-visible:ring-offset-white' : 'peer-focus-visible:ring-white/40 peer-focus-visible:ring-offset-black'
      } ${
        checked
          ? light ? 'bg-[#111] border-[#111] text-white' : 'bg-white border-white text-black'
          : light ? 'bg-white border-[#c9c9c6] text-transparent group-hover:border-[#111]' : 'bg-white/5 border-white/40 text-transparent group-hover:border-white/60'
      }`}
      aria-hidden="true"
    >
      ✓
      {required && (
        <span
          className={`absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full ${light ? 'bg-amber-500' : 'bg-amber-400/85'}`}
          aria-hidden="true"
        />
      )}
    </span>
    <span className={`font-inter text-[13px] leading-snug transition-colors ${
      checked
        ? (light ? 'text-[#3a3a3d]' : 'text-white/85')
        : (light ? 'text-[#57585b] group-hover:text-[#171718]' : 'text-white/65 group-hover:text-white/85')
    }`}>
      {label}
      {required && (
        <span className={`font-bold ml-1.5 text-[11px] uppercase tracking-[0.18em] ${light ? 'text-amber-600' : 'text-amber-400/85'}`}>
          Required
        </span>
      )}
    </span>
  </label>
);
